// --- Shared GraphQL pagination types and utilities ---

/**
 * Pagination result for numbered page-based pagination.
 * Used by all page-level GraphQL hooks (customers, receipts, transactions, etc.).
 */
export interface PaginationResult {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * Full GraphQL relay-style connection type (page-level pagination).
 * Used by list queries that return startCursor, endCursor, hasNextPage, hasPreviousPage.
 */
export interface GraphQLConnection<T> {
  edges: Array<{ node: T }>;
  pageInfo: {
    startCursor: string | null;
    endCursor: string | null;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

/**
 * Reduced GraphQL connection type for cursor-based "load more" pagination.
 * Used by audit log and recent activity hooks that only need endCursor + hasNextPage.
 */
export interface GraphQLCursorConnection<T> {
  edges: Array<{ node: T }>;
  pageInfo: {
    endCursor: string | null;
    hasNextPage: boolean;
  };
}

/**
 * Encode a 1-based page number into a cursor string compatible with the C# BitConverter format.
 * The cursor is a base64-encoded little-endian 32-bit integer of the 0-based page index.
 */
export function pageToCursor(page: number): string {
  const pageIndex = page - 1;
  const buffer = new ArrayBuffer(4);
  const view = new DataView(buffer);
  view.setInt32(0, pageIndex, true); // little-endian to match C# BitConverter
  const bytes = new Uint8Array(buffer);
  return btoa(String.fromCharCode(...bytes));
}

/**
 * Build a GraphQL sort variable from a sort field map, field name, and order.
 * Returns undefined if no sortBy is provided.
 */
export function buildGraphQLSortVariable(
  sortFieldMap: Record<string, string>,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc',
) {
  if (!sortBy) return undefined;
  const field = sortFieldMap[sortBy] || sortBy;
  return [{ field, order: sortOrder === 'desc' ? 'Descending' : 'Ascending' }];
}

/**
 * Compute a PaginationResult from a total item count, current page, and page size.
 * Used by mock data helpers across all page-level hooks.
 */
export function computeMockPagination(totalItems: number, page: number, pageSize: number): PaginationResult {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  return {
    page,
    pageSize,
    totalItems,
    totalPages,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
  };
}

/**
 * Create a default (empty) PaginationResult with the given page size.
 */
export function createDefaultPagination(pageSize: number): PaginationResult {
  return {
    page: 1,
    pageSize,
    totalItems: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  };
}

/**
 * Merge a base pagination (from a list query) with a total count (from a count query),
 * using the caller's requested pageSize to compute totalPages.
 *
 * During page transitions the list query may still be loading, causing basePagination
 * to fall back to a default with a potentially mismatched pageSize. Using
 * requestedPageSize ensures totalPages stays stable and prevents spurious page resets.
 */
export function computePaginationWithCount<T extends PaginationResult>(
  basePagination: T,
  totalCount: number,
  requestedPageSize?: number,
): T {
  if (totalCount <= 0) return basePagination;

  const effectivePageSize = requestedPageSize ?? basePagination.pageSize;
  return {
    ...basePagination,
    totalItems: totalCount,
    totalPages: effectivePageSize > 0 ? Math.ceil(totalCount / effectivePageSize) : 1,
  };
}
